import { DIRECTIONS } from '@veriverse/game-logic';
import { z } from 'zod';

/**
 * Realtime message contracts (spec §8.2).
 * Every client → server message has a zod schema; the server rejects anything that does not parse.
 */

export const CLIENT_MESSAGES = {
  move: 'move',
  chat: 'chat',
  dm: 'dm',
  interact: 'interact',
  typing: 'typing',
  emote: 'emote',
} as const;

/** Server → client message types (state patches are separate; these are one-off events). */
export const SERVER_MESSAGES = {
  chat: 'chat',
  chatHistory: 'chat:history',
  chatRejected: 'chat:rejected',
  dm: 'dm',
  dmRejected: 'dm:rejected',
  announcement: 'announcement',
  rosterChanged: 'roster:changed',
  mapChanged: 'map:changed',
  mapScheduled: 'map:scheduled',
  interactRejected: 'interact:rejected',
  objectEvent: 'object:event',
  typing: 'typing',
  emote: 'emote',
} as const;
export type ClientMessageType = (typeof CLIENT_MESSAGES)[keyof typeof CLIENT_MESSAGES];

/**
 * One tile step in a direction. `seq` lets the client reconcile its prediction with the server;
 * `sprint` asks for the faster sprinting pace (held sprint key).
 */
export const moveMessageSchema = z
  .object({
    seq: z.number().int().nonnegative(),
    dir: z.enum(DIRECTIONS),
    sprint: z.boolean().optional(),
  })
  .strict();
export type MoveMessage = z.infer<typeof moveMessageSchema>;

/**
 * The sender started or stopped typing a nearby-chat message (spec §18, proximity chat
 * visualizers). While typing, the client repeats `true` every `TYPING_HEARTBEAT_MS` so players
 * who walk up see the bubble too.
 */
export const typingMessageSchema = z.object({ typing: z.boolean() }).strict();
export type TypingMessage = z.infer<typeof typingMessageSchema>;

/** Server → client: someone close enough to see you started or stopped typing. */
export interface TypingEvent {
  readonly sessionId: string;
  readonly typing: boolean;
}

/** How often a typing client repeats `typing: true`. */
export const TYPING_HEARTBEAT_MS = 3_000;
/** A typing bubble with no fresh `true` disappears after this long (a lost "stop" never sticks). */
export const TYPING_BUBBLE_TTL_MS = 7_000;

/** The emotes on the wheel, in order from the top, clockwise (LimeZu animated UI sprites). */
export const EMOTE_IDS = [
  'angry',
  'sad',
  'think',
  'mail',
  'up',
  'down',
  'timer',
  'timer_red',
] as const;
export type EmoteId = (typeof EMOTE_IDS)[number];

/** The sender picked an emote on the wheel. */
export const emoteMessageSchema = z.object({ emote: z.enum(EMOTE_IDS) }).strict();
export type EmoteMessage = z.infer<typeof emoteMessageSchema>;

/** Server → client: someone close enough to see you (and you yourself) plays an emote. */
export interface EmoteEvent {
  readonly sessionId: string;
  readonly emote: EmoteId;
}

/** How long an emote stays over the head. */
export const EMOTE_DURATION_MS = 3_000;

/**
 * Options sent with `joinOrCreate`: the short-lived access token from `POST /auth/refresh` and the
 * admission ticket from the queue.
 * The token travels in the join options, never in the URL (spec §10.1).
 */
export const joinOptionsSchema = z
  .object({
    protocolVersion: z.number().int(),
    token: z.string().min(1).max(4096),
    /** One-time admission ticket from the queue (spec §6.2). */
    ticket: z.string().min(1).max(128),
    /** Name of the office (map region) to spawn in; without it the player arrives at their desk or the first spawn. */
    office: z.string().min(1).max(100).optional(),
  })
  .strict();
export type JoinOptions = z.infer<typeof joinOptionsSchema>;

/** Server → client: announcement banner text from an admin. */
export interface AnnouncementEvent {
  readonly text: string;
  readonly at: number;
}

/** Why a chat message was not delivered. */
export interface ChatRejectedEvent {
  readonly reason: 'invalid' | 'rate-limited';
}

/** Presence of an avatar: live players are `online`/`away`; colleagues who are signed out are `offline`. */
export const PLAYER_STATUSES = ['online', 'away', 'offline'] as const;
export type PlayerStatus = (typeof PLAYER_STATUSES)[number];
